// src/client/lib/i18n.ts
//
// Phase B T2d: i18next plumbing (minimal).
//  - 採用: i18next + react-i18next (ADR-UX-016)
//  - 未採用 (T2+): i18next-icu (ICU MessageFormat)、i18next-browser-languagedetector
//  - resources: ja は inline import、 en は切替時に dynamic import (changeLocale、 初回 load を軽くする)
//
// 実体翻訳は **ja 完備 + en placeholder**。文言の段階的展開は別 PR で進める。

import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import jaResources from "../locales/ja.json";

export const SUPPORTED_LOCALES = ["ja", "en"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = "ja";

// 既定 (起動時は常に ja、 永続化なし) 以外の文言は、 切り替えた時に初めて読む (初回 load を軽くする)。
// en.json は約 160 kB あり、 起動時に使われないのに index chunk に入っていた。
const LAZY_RESOURCES: Partial<Record<SupportedLocale, () => Promise<{ default: Record<string, unknown> }>>> = {
  en: () => import("../locales/en.json"),
};

void i18next.use(initReactI18next).init({
  resources: {
    ja: { translation: jaResources },
  },
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES,
  interpolation: {
    // React は値を escape 済のため i18next 側では無効化
    escapeValue: false,
  },
  returnNull: false,
});

export default i18next;

/**
 * 言語を切り替える。 文言がまだ読み込まれていなければ先に読み込んでから切り替える
 * (読み込み前に切り替えると一瞬 ja の fallback 文言が出るため)。
 * 読み込みに失敗した場合 (deploy 直後で旧 chunk が 404 等) は切り替えず reject する。
 */
export async function changeLocale(lng: SupportedLocale): Promise<void> {
  if (!i18next.hasResourceBundle(lng, "translation")) {
    const load = LAZY_RESOURCES[lng];
    if (load) {
      const mod = await load();
      i18next.addResourceBundle(lng, "translation", mod.default, true, true);
    }
  }
  await i18next.changeLanguage(lng);
}

export function isSupportedLocale(value: string | null | undefined): value is SupportedLocale {
  return value === "ja" || value === "en";
}
